import axios from "axios";
// import BaconIcon from '../icons/foodIcons/Bacon.svg'
// import BeefIcon  from '../icons/foodIcons/Beef.svg'
// import BroccoliIcon from '../icons/foodIcons/Broccoli.svg'
// import ChickenIcon  from '../icons/foodIcons/Chicken.svg'
// import CucumberIcon from '../icons/foodIcons/Cucumber.svg'
// import GrapesIcon from '../icons/foodIcons/Grapes.svg'
// import PeachIcon  from '../icons/foodIcons/Peach.svg'
// import  WhiteBreadIcon from '../icons/foodIcons/WhiteBread.svg'


import { 
  PreferenceSchema, DSCommunitySchema, AccountSchema, AuthSchema, MembershipSchema, 
  ResourcePlanSchema, PlanSchema, PlanType, InventorySchema,
  MessageSchema, CredentialSchema, RequirementSchema, CommunitySchema, KitchenSpaceSchema,
  CommunityAgreementSchema,
  KitchenOptionsType,
  ResourceType, FilterType, ResourceSchema,
  ResourceOptionType,
  TrainingInventorySchema,
  FocusGroupSessionResponseSchema,
  FocusGroupSessionSchema,
  RegisteredPartner,
  SelfServiceDetailsSchema,
  ResourceBookingSchema,
  UATResponse,
  FeeSchema,
  CurrentRequirementsSchema,
  DiscountSchema,
  TeamMemberSchema,
  InviteTeamMemberFormDataSchema
} from "../data/types";

import config from '../config';
import { clearAuthVerified, persistAuthTokens } from '../utils/authSession';

const new_api_host = config.apiUrl
const secretKey = process.env.REACT_APP_SECRET_KEY ? process.env.REACT_APP_SECRET_KEY : ""
const apiKey = process.env.REACT_APP_API_KEY ? process.env.REACT_APP_API_KEY : ""

const axiosInstance = axios.create({
  baseURL: new_api_host,
  timeout: 10000,
  headers: {
    "Content-Type": "application/json",
    accept: "application/json",
  },
});

// Helper function to validate and prepare file objects
const isBrowserFile = (value: any): value is File => {
  return typeof File !== 'undefined' && value instanceof File;
};

const prepareFileObject = (value: any, fieldName: string): File | null => {
  // If it's already a File instance
  if (isBrowserFile(value)) {
    return value;
  }
  
  // If it's a nested object with a file property
  if (value && typeof value === 'object' && value.file) {
    if (isBrowserFile(value.file)) {
      return value.file;
    }
  }
  
  // If it's a base64 string, convert to File (common issue)
  if (typeof value === 'string' && value.startsWith('data:')) {
    console.warn(`Base64 string detected for ${fieldName}. This should be converted to File object by processResourceUpdates.`);
    return null;
  }
  
  // If it's a file path or URL, log warning
  if (typeof value === 'string' && (value.includes('/') || value.includes('\\'))) {
    console.warn(`File path detected for ${fieldName}: ${value}. Make sure to send actual File object.`);
    return null;
  }
  
  return null;
};

// Utility function to convert base64 string to File object
export const base64ToFile = (base64String: string, filename: string, mimeType: string): File => {
  const byteCharacters = atob(base64String.split(',')[1]);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  return new File([byteArray], filename, { type: mimeType });
};

// Helper function to detect and convert base64 strings to File objects
const convertBase64ToFile = (value: any, fieldName: string): File | null => {
  if (typeof value === 'string' && value.startsWith('data:')) {
    try {
      // Extract mime type and base64 data
      const matches = value.match(/^data:([^;]+);base64,(.+)$/);
      if (matches) {
        const mimeType = matches[1];
        // const base64Data = matches[2];
        
        // Generate filename based on field name and mime type
        const extension = mimeType.split('/')[1] || 'bin';
        const filename = `${fieldName}_${Date.now()}.${extension}`;
        
        // console.log(`Converting base64 to file: ${fieldName} -> ${filename} (${mimeType})`);
        return base64ToFile(value, filename, mimeType);
      }
    } catch (error) {
      console.error(`Error converting base64 to file for ${fieldName}:`, error);
    }
  }
  return null;
};

// Function to process resource updates and convert base64 strings to files
export const processResourceUpdates = (resourceUpdates: any): any => {
  if (!resourceUpdates || typeof resourceUpdates !== 'object') {
    return resourceUpdates;
  }
  
  const processed = { ...resourceUpdates };
  
  // Process each field
  for (const [key, value] of Object.entries(processed)) {
    // Skip if already a File object
    if (isBrowserFile(value)) {
      continue;
    }
    
    // Handle arrays (specifically bgPhotos array which may contain data URLs)
    if (Array.isArray(value)) {
      if (key === 'bgPhotos' || key === 'bgPhoto') {
        // Process bgPhotos array: convert data URLs to File objects, keep regular URLs as strings
        const processedArray = value.map((item: any, index: number) => {
          // If it's already a File, keep it
          if (isBrowserFile(item)) {
            return item;
          }
          
          // If it's a data URL (base64), convert to File
          if (typeof item === 'string' && item.startsWith('data:')) {
            const fileObject = convertBase64ToFile(item, `${key}_${index}`);
            if (fileObject) {
              console.log(`Converted bgPhotos[${index}] from data URL to File object`);
              return fileObject;
            }
          }
          
          // If it's a regular URL string, keep it as is (don't re-upload existing images)
          // If it's null/undefined/empty, skip it
          if (item && typeof item === 'string' && item.trim() !== '') {
            return item;
          }
          
          return null;
        }).filter((item: any) => item !== null && item !== undefined);
        
        processed[key] = processedArray;
        continue;
      } else {
        // For other arrays, process each item
        processed[key] = value.map((item: any, index: number) => {
          if (isBrowserFile(item)) {
            return item;
          }
          const fileObject = convertBase64ToFile(item, `${key}_${index}`);
          return fileObject || item;
        });
        continue;
      }
    }
    
    // Try to convert base64 string to File (for single values like logo)
    const fileObject = convertBase64ToFile(value, key);
    if (fileObject) {
      processed[key] = fileObject;
      // console.log(`Successfully converted ${key} from base64 to File object`);
    }
    
    // Handle nested objects (like { file: base64String })
    if (value && typeof value === 'object' && 'file' in value && typeof (value as any).file === 'string') {
      const nestedFile = convertBase64ToFile((value as any).file, `${key}_file`);
      if (nestedFile) {
        processed[key] = { file: nestedFile };
        // console.log(`Successfully converted nested file in ${key}`);
      }
    }
  }
  
  return processed;
};

// // Utility function to validate resource updates before sending
// export const validateResourceUpdates = (resourceUpdates: any): { isValid: boolean; issues: string[] } => {
//   const issues: string[] = [];
  
//   if (!resourceUpdates) {
//     issues.push("resourceUpdates is null or undefined");
//     return { isValid: false, issues };
//   }
  
//   // Check for expected file fields
//   const expectedFileFields = ['bgPhoto', 'logo'];
//   expectedFileFields.forEach(field => {
//     if (resourceUpdates[field]) {
//       const fileObject = prepareFileObject(resourceUpdates[field], field);
//       if (!fileObject) {
//         if (typeof resourceUpdates[field] === 'string' && resourceUpdates[field].startsWith('data:')) {
//           issues.push(`${field} is a base64 string and should be converted to File object`);
//         } else {
//           issues.push(`${field} is not a valid File object`);
//         }
//       }
//     }
//   });
  
//   return { isValid: issues.length === 0, issues };
// };

const objectToFormData = (obj: { [key: string]: any }): FormData =>  {
  const processedUpdates = processResourceUpdates(obj);
  
  const formData = new FormData();
  const entries = Object.entries(processedUpdates || {});
  if (entries.length === 0) {
    console.warn("objectToFormData: processedUpdates is empty", { original: obj, processedUpdates });
  }
  for (const [key, value] of entries) {
    // Handle file objects properly using helper function
    const fileObject = prepareFileObject(value, key);
    if (fileObject) {
      formData.append(key, fileObject);
      // console.log(`FormData append [file]: ${key}`);
      continue;
    }

    // Handle arrays (like file arrays, specifically bgPhotos)
    if (Array.isArray(value)) {
      // For bgPhotos array, append each item properly
      if (key === 'bgPhotos' || key === 'bgPhoto') {
        // If the array is empty, append an empty array representation
        if (value.length === 0) {
          formData.append(key, JSON.stringify([]));
          // console.log(`FormData append [bgPhotos empty array]: ${key}`);
        } else {
          value.forEach((item, index) => {
            if (isBrowserFile(item)) {
              // Append File object for new uploads
              formData.append(`${key}_${index}`, item);
              // console.log(`FormData append [bgPhotos file]: ${key}_${index}`);
            } else if (item && typeof item === 'string' && item.trim() !== '') {
              // Append regular URL string for existing images (don't re-upload)
              formData.append(`${key}_${index}`, item);
              // console.log(`FormData append [bgPhotos URL]: ${key}_${index}`);
            } else {
              // Skip null/undefined/empty items
              // console.log(`FormData skip [bgPhotos item]: ${key}_${index}`);
            }
          });
        }
      } else {
        // For other arrays, handle based on content type
        // Check if array contains any file objects
        const hasFiles = value.some(item => isBrowserFile(item));
        
        if (hasFiles) {
          // If array contains files, append each item with indexed keys
          value.forEach((item, index) => {
            if (isBrowserFile(item)) {
              formData.append(`${key}[${index}]`, item);
              // console.log(`FormData append [file[]]: ${key}[${index}]`);
            } else if (item === null || item === undefined) {
              // Skip null/undefined items
            } else if (typeof item === 'object') {
              formData.append(`${key}[${index}]`, JSON.stringify(item));
              // console.log(`FormData append [json[]]: ${key}[${index}]`);
            } else {
              formData.append(`${key}[${index}]`, String(item));
              // console.log(`FormData append [primitive[]]: ${key}[${index}]`);
            }
          });
        } else {
          // For arrays of primitives (strings, numbers, etc.) or objects, serialize as JSON
          // This is the most common and reliable approach for non-file arrays
          formData.append(key, JSON.stringify(value));
          // console.log(`FormData append [array as JSON]: ${key}`);
        }
      }
      continue;
    }

    // Handle null/undefined values
    if (value === null || value === undefined) {
      // console.log(`FormData skip [nullish]: ${key}`);
      continue;
    }

    // Handle plain objects (serialize to JSON)
    if (typeof value === 'object') {
      formData.append(key, JSON.stringify(value));
      // console.log(`FormData append [json]: ${key}`);
      continue;
    }

    // Handle booleans/numbers/strings
    formData.append(key, String(value));
    // console.log(`FormData append [primitive]: ${key}`);
  }

  return formData;
}

// Utility function to check if FormData has entries
const hasFormDataEntries = (formData: FormData): boolean => {
  const entries = formData.entries();
  const firstEntry = entries.next();
  return !firstEntry.done;
}


// Adding API access keys to headers
axiosInstance.defaults.headers.common['HOMECOOK-SECRET-KEY'] = secretKey
axiosInstance.defaults.headers.common['HOMECOOK-API-KEY'] = apiKey


axiosInstance.interceptors.response.use(
  response => response,
  async error => {
    const originalRequest = error.config;

    if (error.response && error.response.status === 401 && error.response.statusText === "Unauthorized") {
        const refresh_token = localStorage.getItem('refresh_token');

        return axiosInstance
            .post('/community/auth', {refresh: refresh_token})
            .then((response) => {

                let data = (response.data && response.data.body) ? response.data.body : response.data

                persistAuthTokens(data.login_token, data.refresh_token)

                // axiosInstance.defaults.headers['Authorization'] = data.login_token
                originalRequest.headers['Authorization'] = data.login_token

                return axiosInstance(originalRequest);
            })
            .catch(err => {
                console.log(err)
            });
    }
    // return Promise.reject(error);
}
)

type KitchenIdParameter = {
  communityId: string
  id: string;
}

type UpdateKitchenParameter = {
  communityId: string;
  spaceId: string;
  kitchenUpdates: DSCommunitySchema | {};
}


type PreferenceParameter = {
  communityId: string;
  data?: PreferenceSchema;
}


const startNewAuth = async (user: AccountSchema) => {
  let data: AccountSchema | null
  let new_user_api_url = `/community/auth`
  if (user?.email || (user?.phone && user.phone.length === 12)){
      var response = await axiosInstance.post(new_user_api_url, user)

      if (response){
          data = (response.data && response.data.body) ? response.data.body : response.data
          if (data){
              persistAuthTokens(data.login_token, data.refresh_token)
          }
          return data
      }
  }
  return null
}



const createNewCommunity = async (community: CommunitySchema, account: AccountSchema) => {
  if (community && account){
    let fetch_api_url = `/community/admin`;
    let adminCommunity = {...community, ...account}
    // console.log("adminCommunity", adminCommunity)
    var communityForm = objectToFormData(adminCommunity)
    try {
      let response = await axiosInstance.post(fetch_api_url, communityForm, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      if (response && response.status === 201) {
        console.log("create success");
        return response.data
      }
    } catch (err) {
      console.log(err);
      return null
    }
  }
}


export const updateCommunity = async (communityId: string, communityUpdates: CommunitySchema) => {
  if (communityId && communityUpdates){
    let apiUrl = `/community/${communityId}`;
    var communityForm = objectToFormData(communityUpdates)
    try {
      let response = await axiosInstance.put(apiUrl, communityForm, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      if (response && response.status === 201) {
        console.log("create success");
        return response.data
      }
    } catch (err) {
      console.log(err);
      return null
    }
  }
}


const updateUserData = async (communityId: string, username: string, accountUpdates: AccountSchema) => {
  // console.log("updateUserData", communityId, username, accountUpdates)
  let data: AccountSchema | null = null
  let updateUserApiUrl = `/community/${communityId}/account/${username}`
  const accountUpdatesForm = objectToFormData(accountUpdates)
  // Debug: Log FormData contents
  // console.log("FormData entries:");
  const entries = accountUpdatesForm.entries();
  let entry = entries.next();
  while (!entry.done) {
    // const [key, value] = entry.value;
    // console.log(`${key}:`, value);
    entry = entries.next();
  }
  const shouldSend = communityId && username && hasFormDataEntries(accountUpdatesForm);
  // console.log("updateUserData pre-check:", { communityId, username, shouldSend });
  // console.log("entries", entries)
  // console.log("update accountUpdatesForm", accountUpdatesForm)
  if (shouldSend){
      let response = await axiosInstance.patch(updateUserApiUrl, accountUpdatesForm, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
      if (response){
          data = (response.data && response.data.body) ? response.data.body : response.data
          return data
      }
  }
  return data
}


const getAuthUser = async ({phone, email, token_username}: AuthSchema) => {
  let data: AccountSchema | null
  let new_user_api_url = `/community/auth`

  let payload = {}

  if (phone){
    payload = {phone}
  } else if (email){
    payload = {email}
  } else if (token_username){
    payload = {token_username}
  }

  if (payload && (phone || email || token_username)) {
    let response = await axiosInstance.post(new_user_api_url, payload)
    if (response){
        data = (response.data && response.data.body) ? response.data.body : response.data
        return data
    }
  }
  return null
}


export const verifyEmailInviteToken = async (communityId: string, email: string, token: string) => {
  const apiUrl = `/community/${communityId}/verify/invite`
  if (communityId && email && token){
    const response = await axiosInstance.post(apiUrl, {email}, 
      {
        headers: {
          'Content-Type': 'application/json',
          // 'Authorization': `Bearer ${token}`
          'x-invite-token': `${token}`
        }
      }
    )
    if (response){
      return response.data
    }
  }
  return null
}


const verifyUserOTP = async (otp_code: string, username: string) => {
  let data: AccountSchema | null
  let new_user_api_url = `/community/auth/verify`
  // console.log("otp_code, phone", otp_code, username)

  if (otp_code){
      let response = await axiosInstance.post(new_user_api_url, {otp_code, username})
      // console.log('response token_username', response)
      if (response){
          data = (response.data && response.data.body) ? response.data.body : response.data
          return data
      }
  }
  return null
}


const fetchCommunity = async (communityId: string) => {
  let data: CommunitySchema | null
  let community_api_url = `/community/${communityId}`
  if (communityId){
      let response = await axiosInstance.get(community_api_url)
      // console.log('response', response)
      if (response){
          data = (response.data && response.data.body) ? response.data.body : response.data
          return data
      }
  }
  return null
}


const signOut = () => {
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  clearAuthVerified()
}


// GET PREFERENCES
const getPreferences = async ({communityId}: {communityId: string}) => {
  let fetch_api_url = `/community/${communityId}/preferences`

  try{
    let response = await axiosInstance.get(fetch_api_url);
    if (response) {
      let data = response.data;
      return data;
    }
    return []
  }catch(err){
    console.log(err);
  }
}


// GET MEMBER REVIEWS
const getMemberReviews = async (uid: string) => {
  let fetchMemberReviewsApiUrl = `/community/all/review/${uid}`

  try {
    let response = await axiosInstance.get(fetchMemberReviewsApiUrl);
    if (response && response.data) {
      return response.data;
    }
  }catch(err){
    console.log(err);
  }
  return []
}


// CREATE PREFERENCES
const createPreference = async (communityId: string, data: PreferenceSchema) => {
  let fetch_api_url = `/community/${communityId}/preferences`

  try{
    let response = await axiosInstance.post(fetch_api_url,data);
    if (response) {
      console.log("create success");
    }
  } catch (err) {
    console.log(err);
  }
}


// UPDATE PREFERENCES 
const updatePreferences = async ({communityId, data}: PreferenceParameter) => {
  let fetch_api_url = `/community/${communityId}/preferences`
  try{
    let response = await axiosInstance.patch(fetch_api_url, data);
    if (response) {
      console.log("update success");
    }
  } catch (err) {
    console.log(err);
  }
}


// GET ALL KITCHENS
const getAllKitchens = async (communityId: string) => {
  let data: KitchenSpaceSchema[] = []
  if (communityId){
    let fetch_api_url = `/community/${communityId}/kitchens`;

    try{
      let response = await axiosInstance.get(fetch_api_url);
      if (response) {
        let data = response.data;
        return data;
      }
      
    } catch(err){
      console.log(err);
    }
  }
  return data
}


// GET ALL MEMBERSHIP PLANS
const getMembershipPlans = async (communityId: string) => {
  if (communityId){
    let fetch_api_url = `/community/${communityId}/plans`;

    try{
      let response = await axiosInstance.get(fetch_api_url);
      if (response) {
        let data = response.data;
        return data;
      }
      return []
    }catch(err){
      console.log(err);
    }
  }
}


export const updatePlan = async (communityId:string, planType: PlanType, planId: string, planData: PlanSchema | ResourcePlanSchema) => {
  if (communityId){
    // console.log("update plan data", planData);
    let apiUrl = `/community/${communityId}/${planType}/plans/${planId}`;

    try{
      let response = await axiosInstance.put(apiUrl,planData);
      // console.log("response", response)
      if (response) {
        // console.log("Plan update success!");
        const data = (response.data && response.data.body) ? response.data.body : response.data
        // console.log("response data", data)
        return data;
      }
    } catch(err){
      console.error(`Error was found while updating: ${err}`);
      return null
    }
  }
  return null
}

const getMembershipPlan = async (communityId:string, planId: string) => {
  if (communityId){
    let apiUrl = `/community/${communityId}/plans/${planId}`;

    try{
      let response = await axiosInstance.get(apiUrl);
      if (response) {
        console.log("Plan get success!");
        let data = response.data;
        return data;
      }
    }catch(err){
      console.log(err);
    }
  }
}


/** Add member (community) agreement; returns object with presigned url for the .docx file. */
export const addMemberAgreement = async (communityId: string, agreement: CommunityAgreementSchema): Promise<CommunityAgreementSchema | null> => {
  if (!communityId) return null;
  const apiUrl = `/community/${communityId}/agreements`;
  try {
    const response = await axiosInstance.post(apiUrl, agreement);
    if (response?.data) {
      return response.data;
    }
  } catch (err) {
    console.error("addMemberAgreement error:", err);
  }
  return null;
}


/** Get member (community) agreement; returns object with presigned url for the .docx file. */
export const getMemberAgreement = async (communityId: string): Promise<CommunityAgreementSchema | null> => {
  if (!communityId) return null;
  const apiUrl = `/community/${communityId}/agreements`;
  try {
    const response = await axiosInstance.get(apiUrl);
    if (response?.data) {
      const data = (response.data?.body != null) ? response.data.body : response.data;
      return data as CommunityAgreementSchema;
    }
  } catch (err) {
    console.error("getMemberAgreement error:", err);
  }
  return null;
}

const addCommunityPlans = async (communityId: string, plan: PlanSchema | {}) => {
  if (communityId){
    let apiUrl = `/community/${communityId}/plans`;
    try {
      let response = await axiosInstance.post(apiUrl, plan)
      if (response && response.status === 201) {
        console.log("create success");
        return response.data
      }
    } catch (err) {
      console.log(err);
    }
  }
  return null
}

export const deleteMembershipPlan = async (communityId: string, planId: string) => {
  if (communityId){
    let delete_api_url = `/community/${communityId}/plans/${planId}`;
    try {
      let response = await axiosInstance.delete(delete_api_url);
      if (response && response.status === 200) {
        const data = (response.data && response.data.body) ? response.data.body : response.data
        console.log("delete success");
        return data
      }
    } catch (error) {
      console.error("Error deleting plan:", error);
    }
  }
  return null
}

const getCommunityRequirements = async (communityId: string) => {
  if (communityId){
    let fetchApiUrl = `/community/${communityId}/requirements`;
    // console.log("fetchApiUrl", fetchApiUrl)
    try{
      let response = await axiosInstance.get(fetchApiUrl);
      // console.log("requirements response", response)
      if (response && response.status === 200) {
        const data = (response.data && response.data.body) ? response.data.body : response.data
        return data;
      }
      return []
    }catch(err){
      console.log(err);
    }
  }
}

const deleteMembershipRequirement = async (communityId: string, reqId: string) => {
  if (communityId){
    let delete_api_url = `/community/${communityId}/requirements/${reqId}`;
    try {
      let response = await axiosInstance.delete(delete_api_url);
      if (response) {
        console.log("delete success");
      }
    } catch (error) {
      console.error("Error deleting kitchen:", error);
    }
  }
}

const addMembershipRequirement = async (communityId: string, requirement: RequirementSchema) => {
  if (communityId){
    let fetch_api_url = `/community/${communityId}/requirements`;
    try {
      // console.log(requirement);
      let response = await axiosInstance.post(fetch_api_url, requirement)
      if (response && response.status === 201) {
        console.log("create success");
        return response.data
      }
    } catch (err) {
      console.log(err);
      return null
    }
  }
}

export const addRequirements = async (communityId: string, requirements: RequirementSchema[]) => {
  if (communityId){
    let fetch_api_url = `/community/${communityId}/requirements`;
    try {
      let response = await axiosInstance.put(fetch_api_url, {requirements})
      if (response && response.status === 201) {
        console.log("create success");
        return response.data
      }
    } catch (err) {
      console.log(err);
      return null
    }
  }
  return null
}


export const getAllResources = async (communityId: string, resourceType?: ResourceType, filter?: FilterType) => {
  let data: ResourceSchema[] | null
  
  var resource_api_url = `/community/${communityId}/resource`
  resource_api_url = resourceType ? `${resource_api_url}?resource_type=${resourceType}` : resource_api_url

  let response = await axiosInstance.get(resource_api_url)
  if (response){
      data = (response.data && response.data.body) ? response.data.body : response.data
      return data
  }
  return null
}


export const addNewResource = async (communityId: string, resource: ResourceSchema | {}) => {
  if (communityId){
    var resource_api_url = `/community/${communityId}/resource`
    var resourceForm = objectToFormData(resource)
    try {
      let response = await axiosInstance.post(resource_api_url, resourceForm, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      if (response && response.status === 201) {
        console.log("create success");
        return response.data
      }
    } catch (err) {
      console.log(err);
      return null
    }
  }
}


export const getResourceByID = async (communityId: string, resourceId: string) => {
  let data: ResourceSchema
  var resource_api_url = `/community/${communityId}/resource/${resourceId}`
  let response = await axiosInstance.get(resource_api_url)
  if (response){
      data = (response.data && response.data.body) ? response.data.body : response.data
      return data
  }
  return {} as ResourceSchema
}


export const updateResourceById = async (communityId: string, resourceId: string, resourceUpdates: ResourceSchema) => {
  // console.log("resourceUpdates", resourceUpdates)
  var resourceForm = objectToFormData(resourceUpdates)
  // console.log("resourceForm", resourceForm)
  if (communityId && resourceId && resourceForm){
    var resource_api_url = `/community/${communityId}/resource/${resourceId}`
    try {
      let response = await axiosInstance.patch(resource_api_url, resourceForm, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      if (response) {
        console.log("update success");
        return response.data
      }
    } catch (error) {
      console.error(`Error was found while updating: ${error}`);
      return null
    }
  }
  return null
};

// export const updateResourceById = async (communityId: string, resourceId: string, resourceUpdates: ResourceSchema) => {
//   console.log("=== updateResourceById Debug Info ===")
//   console.log("communityId:", communityId)
//   console.log("resourceId:", resourceId)
//   console.log("Original resourceUpdates:", resourceUpdates);
  
//   // Process and convert base64 strings to File objects
//   const processedUpdates = processResourceUpdates(resourceUpdates);
//   console.log("Processed resourceUpdates:", processedUpdates);
  
//   // Validate resource updates after processing
//   const validation = validateResourceUpdates(processedUpdates);
//   if (!validation.isValid) {
//     console.error("Resource updates validation failed:", validation.issues);
//     console.error("Please ensure all file fields contain valid File objects");
//     return null;
//   }
  
//   // Log file information for debugging
//   if (processedUpdates) {
//     for (const [key, value] of Object.entries(processedUpdates)) {
//       const fileObject = prepareFileObject(value, key);
//       if (fileObject) {
//         console.log(`File found: ${key}`, {
//           name: fileObject.name,
//           size: fileObject.size,
//           type: fileObject.type
//         });
//       }
//     }
//   }
  
//   var resourceForm = objectToFormData(processedUpdates);
  
//   if (communityId && resourceId && resourceForm){
//     var resource_api_url = `/community/${communityId}/resource/${resourceId}`;
//     console.log("API URL:", resource_api_url);
    
//     try {
//       let response = await axiosInstance.patch(resource_api_url, resourceForm, {
//         headers: {
//           'Content-Type': 'multipart/form-data'
//         }
//       });
//       if (response) {
//         console.log("update success");
//         return response.data;
//       }
//     } catch (error: any) {
//       console.error(`Error was found while updating: ${error}`);
//       if (error.response) {
//         console.error("Response data:", error.response.data);
//         console.error("Response status:", error.response.status);
//       }
//       return null;
//     }
//   }
//   return null;
// };


export const deleteCommunityResource = async (communityId: string, resourceId: String) => {
  if (communityId && resourceId){
    const deleteApiUrl = `/community/${communityId}/resource/${resourceId}`;
    try {
      let response = await axiosInstance.delete(deleteApiUrl);
      if (response) {
        console.log("delete success");
      }
    } catch (error) {
      console.error("Error deleting resource:", error);
    }
  }
}


const getKitchenID = async ({communityId, id }: KitchenIdParameter) => {
  var data: KitchenSpaceSchema
  if (communityId && id){
    // console.log("communityId", communityId)
    let fetch_api_url = `/community/${communityId}/kitchens/${id}`;

    let response = await axiosInstance.get(fetch_api_url);
    if (response) {
      data = response.data;
      return data;
    }
  }
  return {} as KitchenSpaceSchema;
}


const addKitchen = async (communityId: string, kitchen: DSCommunitySchema | {}) => {
  if (communityId){
    let fetch_api_url = `/community/${communityId}/kitchens/`;
    var kitchenForm = objectToFormData(kitchen)
    try {
      let response = await axiosInstance.post(fetch_api_url, kitchenForm, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      if (response && response.status === 201) {
        console.log("create success");
        return response.data
      }
    } catch (err) {
      console.log(err);
      return null
    }
  }
}


const addCommunityMember = async (communityId: string, membership: MembershipSchema | {}) => {
  if (communityId){
    let fetch_api_url = `/community/${communityId}/members`;
    try {
      let response = await axiosInstance.post(fetch_api_url, membership);
      if (response && response.status === 201) {
        console.log("create success");
        return response.data
      }
    } catch (err) {
      console.log(err);
      return null
    }
  }
}

const addCommunityMembers = async (communityId: string, memberships: MembershipSchema[] | {}) => {
  if (communityId){
    let fetch_api_url = `/community/${communityId}/members/many`;
    try {
      let response = await axiosInstance.post(fetch_api_url, memberships);
      if (response && response.status === 201) {
        console.log("create success");
        return response.data
      }
    } catch (err) {
      console.log(err);
      return null
    }
  }
}


const deleteKitchen = async ({communityId, id }: KitchenIdParameter) => {
  if (communityId){
    let delete_api_url = `/community/${communityId}/kitchens/${id}`;

    try {
      let response = await axiosInstance.delete(delete_api_url);
      if (response) {
        console.log("delete success");
      }
    } catch (error) {
      console.error("Error deleting kitchen:", error);
    }
  }
}


const updateKitchen = async ({communityId, spaceId, kitchenUpdates}: UpdateKitchenParameter) => {
  if (communityId){
    let update_api_url = `/community/${communityId}/kitchens/${spaceId}`;
    var kitchenForm = objectToFormData(kitchenUpdates)
    try {
      let response = await axiosInstance.put(update_api_url, kitchenForm, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      if (response) {
        console.log("update success");
        return response.data
      }
    } catch (error) {
      console.error(`Error was found while updating: ${error}`);
      return null
    }
  }
};


const updateKitchenOptions = async (communityId: string, spaceId: string, kitchenUpdates: KitchenOptionsType | ResourceOptionType) => {
  if (communityId){
    let update_api_url = `/community/${communityId}/options/${spaceId}`
    // var kitchenForm = objectToFormData(kitchenUpdates)
    // console.log("updateKitchenOptions kitchenUpdates", kitchenUpdates);
    try {
      let response = await axiosInstance.post(update_api_url, kitchenUpdates);
      if (response) {
        console.log("update success");
        return response.data
      }
    } catch (error) {
      console.error(`Error was found while updating: ${error}`);
      return null
    }
  }
};


export const updateRulesForResource = async (communityId: string, resourceId: string, rules: string[]) => {
  if (communityId && resourceId){
    let fetch_api_url = `/community/${communityId}/resource/${resourceId}/rules`
    try {
      let response = await axiosInstance.put(fetch_api_url, {rules});
      return response.data
    } catch (error) {
      console.error(`Error was found while updating: ${error}`);
      return null
    }
  }
}

export const updateResourceOptions = async (communityId: string, resourceId: string, optionUpdates: ResourceOptionType) => {
  if (communityId && resourceId){
    let apiUrl = `/community/${communityId}/resource/${resourceId}/options`
    try {
      let response = await axiosInstance.post(apiUrl, optionUpdates);
      if (response) {
        console.log("update success response", response);
        return response.data
      }
    } catch (error) {
      console.error(`Error was found while updating: ${error}`);
      return null
    }
  }
};


export const updateResourceFee = async (communityId: string, resourceId: string, fee: FeeSchema) => {
  if (communityId && resourceId){
    let fetch_api_url = `/community/${communityId}/resource/${resourceId}/fees`;
    try {
      let response = await axiosInstance.put(fetch_api_url, fee);
      if (response && response.status === 201) {
        console.log("create success");
        return response.data
      }
    } catch (err) {
      console.log(err);
      return null
    }
  }
}

export const deleteResourceFee = async (communityId: string, resourceId: string, feeName: string) => {
  if (communityId && resourceId && feeName){
    let apiUrl = `/community/${communityId}/resource/${resourceId}/fees?is_delete=${true}`
    try {
      let response = await axiosInstance.put(apiUrl, {name: feeName})
      if (response) {
        console.log("delete success");
        return response.data
      }
    } catch (error) {
      console.error(`Error was found while deleting: ${error}`);
      return null
    }
  }
} 


export const updateResourceDiscount = async (communityId: string, resourceId: string, discount: DiscountSchema) => {
  if (communityId && resourceId){
    let fetch_api_url = `/community/${communityId}/resource/${resourceId}/discounts`;
    try {
        let response = await axiosInstance.put(fetch_api_url, discount);
      if (response && response.status === 201) {
        console.log("create success");
        return response.data
      }
    } catch (err) {
      console.log(err);
      return null
    }
  }
}


export const deleteResourceDiscount = async (communityId: string, resourceId: string, discountName: string) => {
  if (communityId && resourceId && discountName){
    let apiUrl = `/community/${communityId}/resource/${resourceId}/discounts?is_delete=${true}`
    try {
      let response = await axiosInstance.put(apiUrl, {name: discountName})
      if (response) {
        console.log("delete success");
        return response.data
      }
    } catch (error) {
      console.error(`Error was found while deleting: ${error}`);
      return null
    }
  }
} 


export const updatePlansForResource = async (communityId: string, resourceId: string, plans: string[]) => {
  if (communityId && resourceId){
    let apiUrl = `/community/${communityId}/resource/${resourceId}/plans`
    try {
      let response = await axiosInstance.put(apiUrl, {plans});
      if (response) {
        console.log("update success response", response);
        return response.data
      }
    } catch (error) {
      console.error(`Error was found while updating: ${error}`);
      return null
    }
  }
};


export const addResourcePlan = async (communityId: string, resourceId: string, plan: ResourcePlanSchema) => {
  if (communityId && resourceId){
    let apiUrl = `/community/${communityId}/resource/${resourceId}/plans`
    try {
      let response = await axiosInstance.post(apiUrl, plan);
      if (response && response.status === 201) {
        const data = (response.data && response.data.body) ? response.data.body : response.data
        // console.log("add success response", data);
        return data
      }
    } catch (error) {
      console.error(`Error was found while adding: ${error}`);
    }
  }
  return null
};


export const updateRequirementsForResource = async (communityId: string, resourceId: string, requirements: string[]) => {
  // console.log("updateRequirementsForResource", requirements)
  if (communityId && resourceId){
    let apiUrl = `/community/${communityId}/resource/${resourceId}/requirements`
    try {
      let response = await axiosInstance.put(apiUrl, {requirements});
      if (response) {
        console.log("update success response", response);
        return response.data
      }
    } catch (error) {
      console.error(`Error was found while updating: ${error}`);
      return null
    }
  }
};


// GET ALL COMMUNITY INVENTORY
const getCommunityAllInventory = async (communityId: string) => {
  if (communityId){
    let fetch_api_url = `/community/${communityId}/inventory`;

    try{
      let response = await axiosInstance.get(fetch_api_url);
      if (response) {
        let data = response.data;
        return data;
      }
      return []
    }catch(err){
      console.log(err);
    }
  }
}


// GET ALL RESOURCE INVENTORY
export const getAllResourceInventory = async (communityId: string, resourceId: string) => {
  if (communityId){
    let fetch_api_url = `/community/${communityId}/resource/${resourceId}/inventory`;
    try{
      let response = await axiosInstance.get(fetch_api_url);
      if (response) {
        const data = (response.data && response.data.body) ? response.data.body : response.data;
        return data;
      }
      return []
    }catch(err){
      console.log(err);
    }
  }
  return []
}


// const addCommunityInventory = async (communityId: string, inventory: InventorySchema) => {
//   if (communityId && inventory){
//     var inventoryForm = objectToFormData(inventory)
//     let post_api_url = `/community/${communityId}/inventory`;
//     try {
//       let response = await axiosInstance.post(post_api_url, inventoryForm, {
//         headers: {
//           'Content-Type': 'multipart/form-data'
//         }
//       });
//       if (response && response.status === 201) {
//         console.log("create success");
//         return response.data
//       }
//     } catch (err) {
//       console.log(err);
//       return null
//     }
//   }
// }

// DELETE RESOURCE INVENTORY ITEM
export const deleteInventoryItemById = async (communityId: string, resourceId: string, itemId: string) => {
  if (communityId){
    let apiUrl = `/community/${communityId}/resource/${resourceId}/inventory/${itemId}`;
    try{
      let response = await axiosInstance.delete(apiUrl);
      if (response) {
        let data = response.data;
        return data;
      }
      return null
    }catch(err){
      console.log(err);
    }
  }
}


export const addResourceInventory = async (communityId: string, resourceId: string, inventoryItem: InventorySchema | TrainingInventorySchema) => {
  // console.log("inventoryItem", inventoryItem)
  if (communityId && resourceId && inventoryItem){
    var inventoryItemForm = objectToFormData(inventoryItem)
    const apiUrl = `/community/${communityId}/resource/${resourceId}/inventory`
    try {
      let response = await axiosInstance.post(apiUrl, inventoryItemForm, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      if (response && response.status === 201) {
        console.log("create success");
        return response.data
      }
    } catch (err) {
      console.log(err);
      return null
    }
  }
}

export const updateResourceInventory = async (communityId: string, resourceId: string, itemId: string, inventoryItem: InventorySchema | TrainingInventorySchema) => {
  // console.log("inventoryItem", inventoryItem)
  if (communityId && resourceId && inventoryItem){
    var inventoryItemForm = objectToFormData(inventoryItem)
    const apiUrl = `/community/${communityId}/resource/${resourceId}/inventory/${itemId}`
    try {
      let response = await axiosInstance.put(apiUrl, inventoryItemForm, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      if (response && response.status === 201) {
        console.log("update success");
        return response.data
      }
    } catch (err) {
      console.log(err);
      return null
    }
  }
}


// GET ALL ACTIVE MEMBERSHIPS
const getMemberships = async (communityId: string) => {
  var data: MembershipSchema[] = []
  if (communityId){
    let fetch_api_url = `/community/${communityId}/members`;

    try{
      let response = await axiosInstance.get(fetch_api_url);
      if (response) {
        data = response.data;
        return data;
      }
      
    }catch(err){
      console.log(err);
    }
  }
  return data
}


// GET INACTIVE MEMBERSHIPS
const getMembershipRequests = async (communityId: string) => {
  var data: MembershipSchema[] = []
  if (communityId){
    let fetch_api_url = `/community/${communityId}/members/requests`

    try{
      let response = await axiosInstance.get(fetch_api_url);
      if (response && response?.data) {
        data = response.data
      }
      
    } catch(err){
      console.log(err);
    }
  }
  return data
}



// CREATE NEW BOOKING
const createBooking = async (resourceId: string, new_booking: ResourceBookingSchema) => {
  let { community_id: communityId } = new_booking
  let booking_api_url = `/community/${communityId}/resource/${resourceId}/booking`
  console.log("createBooking booking_api_url", booking_api_url)
  console.log("createBooking new_booking", new_booking)

  if (communityId){
      let response = await axiosInstance.post(booking_api_url, new_booking)
      // console.log('createBooking response', response)
      if (response){
          return response.data
      }
  }
  return null
}


// FETCH COMMUNITY TRANSACTIONS
export const fetchCommunityTransactions = async (communityId: string, resourceType?: ResourceType) => {
  let data: ResourceBookingSchema[] = []
  if (communityId){
      // communityId = "resources"
      const transactionsApiUrl = `/community/${communityId}/resources/${resourceType}/transactions`
      let response = await axiosInstance.get(transactionsApiUrl)
      if (response){
          data = (response.data && response.data.body) ? response.data.body : response.data
          return data
      }
  }
  return data
}


// FETCH COMMUNITY BOOKINGS
const fetchCommunityBookings = async (communityId: string, resourceType?: ResourceType) => {
  let data: ResourceBookingSchema[] = []
  if (communityId){
      // communityId = "resources"
      const bookingApiUrl = `/community/${communityId}/resources/${resourceType}/bookings`
      let response = await axiosInstance.get(bookingApiUrl)
      if (response){
          data = (response.data && response.data.body) ? response.data.body : response.data
          return data
      }
  }
  return data
}


// FETCH RESOURCE BOOKINGS
export const fetchResourceBookings = async (communityId: string, resourceId: string) => {
  let data: ResourceBookingSchema[] = []
  if (communityId){
      const bookingApiUrl = `/community/${communityId}/resource/${resourceId}/bookings`
      let response = await axiosInstance.get(bookingApiUrl)
      if (response){
          data = (response.data && response.data.body) ? response.data.body : response.data
          return data
      }
  }
  return data
}

// FETCH MEMBER BOOKING
const fetchBookingByID = async (communityId: string, bookingId: string) => {
  let data: ResourceBookingSchema | null
  let booking_api_url = `/community/${communityId}/bookings/${bookingId}`
  if (communityId && bookingId){
      let response = await axiosInstance.get(booking_api_url)
      // console.log('response', response)
      if (response){
          data = (response.data && response.data.body) ? response.data.body : response.data
          return data
      }
  }
  return null
}


// UPDATE MEMBER BOOKING
const updateBookingData = async (communityId: string, bookingId: string, bookingUpdate: ResourceBookingSchema) => {
  let data: ResourceBookingSchema | null
  // console.log("updateBookingData", bookingUpdate)
  // console.log("communityId", communityId)
  // console.log("bookingId", bookingId)
  if (communityId && bookingId){
      let booking_api_url = `/community/${communityId}/bookings/${bookingId}`
      let response = await axiosInstance.patch(booking_api_url, bookingUpdate)
      console.log('updateBooking response', response)
      if (response){
          data = (response.data && response.data.body) ? response.data.body : response.data
          return data
      }
  }
  return null
} 

// FETCH MEMBER PROFILE
const fetchMemberByID = async (communityId: string, uid: string) => {
  let data: MembershipSchema | null
  if (communityId && uid){
      let fetchMemberApiUrl = `/community/${communityId}/members/${uid}`
      let response = await axiosInstance.get(fetchMemberApiUrl)
      // console.log('response', response)
      if (response){
          data = (response.data && response.data.body) ? response.data.body : response.data
          return data
      }
  }
  return null
}

// UPDATE MEMBER PROFILE
const updateCommunityMembership = async (uid: string, communityId: string, membershipUpdate: MembershipSchema) => {
  let data: MembershipSchema | null
  // let username = membershipUpdate?.username
  if (communityId && uid){
      let updateMemberApiUrl = `/community/${communityId}/members/${uid}`
      delete membershipUpdate.username; 
      let response = await axiosInstance.patch(updateMemberApiUrl, membershipUpdate)
      console.log('updateCommunityMembership response', response)
      if (response){
          data = (response.data && response.data.body) ? response.data.body : response.data
          return data
      }
  }
  return null
}


// Fetch All Message Threads
const getAllMessages = async (communityId: string) => {
  let data: MessageSchema[]
  
  if (communityId){
      let api_url = `/community/${communityId}/messages`
      let response = await axiosInstance.get(api_url)
      if (response){
          data = (response.data && response.data.body) ? response.data.body : response.data
          return data
      }
  }
  return []
}

const fetchMemberCredentials = async (communityId: string, uid: string) => {
  let data: CredentialSchema[] = []
  try {
    if (communityId && uid){
    let fetch_api_url = `/community/${communityId}/documents/${uid}`
    let response = await axiosInstance.get(fetch_api_url)
      if (response){
          data = (response.data && response.data.body) ? response.data.body : response.data
          return data
      }
    }
  }
  catch (err) {
    console.log(err)
  }
  return data
}


export const getMemberRequirementsStatus = async (uid: string, communityId: string) => {
  let data: CurrentRequirementsSchema[] = []
  let apiUrl = `/user/${uid}/requirements/${communityId}`
  if (uid){
      let response = await axiosInstance.get(apiUrl)
      if (response){
          data = (response.data && response.data.body) ? response.data.body : response.data
          return data
      }
  }
  return null
}


export const updateMemberRequirementCredential = async (uid: string, communityId: string,  credentialId: string, updatedCredentialDetails: CurrentRequirementsSchema) => {
  let data: CurrentRequirementsSchema | null
  if (uid && communityId && credentialId){
    let apiUrl = `/user/${uid}/requirements/${communityId}/credential/${credentialId}`
    let response = await axiosInstance.patch(apiUrl, updatedCredentialDetails)
    if (response){
      data = (response.data && response.data.body) ? response.data.body : response.data
      return data
    }
  }
  return null
}


const addMembershipCredentials = async (communityId: string, uid: string, cred: CredentialSchema) => {
  if (communityId && uid && cred){
    let fetch_api_url = `/community/${communityId}/documents/${uid}/verify/new`
    var credForm  = objectToFormData(cred)
    try {
      let response = await axiosInstance.post(fetch_api_url, credForm)
      if (response && response.status === 201) {
        console.log("create success");
        return response.data
      }
    } catch (err) {
      console.log(err);
      return null
    }
  }
}


// Fetch Messages in User Thread
const getUserMessages = async (communityId: string, uid: string) => {
  let data: MessageSchema[] | null
  
  if (communityId){
      let api_url = `/community/${communityId}/messages/${uid}`
      let response = await axiosInstance.get(api_url)
      if (response){
          data = (response.data && response.data.body) ? response.data.body : response.data
          return data
      }
  }
  return null
}


// Send Message to New or Existing User
const sendMessageToUser = async (communityId: string, uid: string, content: string) => {
  let data: MessageSchema | null
  let api_url = `/community/${communityId}/messages/${uid}`

  if (content){
      let response = await axiosInstance.post(api_url, {content})
      if (response){
          data = (response.data && response.data.body) ? response.data.body : response.data
          return data
      }
  }
  return null
}


const generateAIResponse = async (communityId: string, uid: string, content: string) => {
  let data: MessageSchema | null
  let aiApiUrl = `/community/${communityId}/messages/${uid}/ai` 

  if (content){
      let response = await axiosInstance.post(aiApiUrl, {content})
      // console.log('response token_username', response)
      if (response){
          // console.log("generateAIResponse response", response)
          data = (response.data && response.data.body) ? response.data.body : response.data
          // console.log("generateAIResponse data", data)
          return data
      }
  }
  return null
}


const fetchPaymentMethods = async (uid: string) => {
  try {
    if (uid){
    let fetchPaymentMethodsApiUrl = `/community/account/${uid}/paymethods`
    let response = await axiosInstance.get(fetchPaymentMethodsApiUrl)
      if (response && response.data){
          let data = (response.data && response.data.body) ? response.data.body : response.data
          return data
      }
    }
  }
  catch (err) {
    console.log(err)
  }
  return []
  // return samplePaymentMethods
}

export const getTeamMembers = async (communityId: string) => {
  let data: TeamMemberSchema[] = []
  let apiUrl = `/community/${communityId}/team`;
    if (communityId){
    const response = await axiosInstance.get(apiUrl);
    if (response){
      data = (response.data && response.data.body) ? response.data.body : response.data
      return data
    }
  }
  return data
}

export const inviteTeamMember = async (communityId: string, teamMember: InviteTeamMemberFormDataSchema) => {
  let data: TeamMemberSchema | null
  let apiUrl = `/community/${communityId}/team/invite`
  if (communityId){
    const response = await axiosInstance.post(apiUrl, teamMember, {
      headers: {
        'Content-Type': 'application/json'
      }
    })
    // console.log("inviteTeamMember response", response)
    if (response){
      data = (response.data && response.data.body) ? response.data.body : response.data
      return data
    }
  }
  return null
}

export type UpdateTeamMemberPayload = {
  email: string
  permissions?: TeamMemberSchema["permissions"]
  role?: TeamMemberSchema["role"]
}

/** Soft-remove a community team member by email. */
export const deleteTeamMember = async (communityId: string, email: string) => {
  if (!communityId || !email) {
    return null
  }
  const response = await axiosInstance.delete(`/community/${communityId}/team`, {
    data: { email },
    headers: { "Content-Type": "application/json" },
  })
  return response?.data?.body ? response.data.body : response?.data ?? null
}

/** Patch a team member's permissions or role by email. */
export const updateTeamMember = async (communityId: string, payload: UpdateTeamMemberPayload) => {
  if (!communityId || !payload.email) {
    return null
  }
  const response = await axiosInstance.patch(`/community/${communityId}/team`, payload, {
    headers: { "Content-Type": "application/json" },
  })
  return response?.data?.body ? response.data.body : response?.data ?? null
}


export const getPastSessionResponses = async (communityId: string, sessionId: string) => {
  let data: FocusGroupSessionResponseSchema[] = []
  let apiUrl = `/focus-group-session/${communityId}/${sessionId}/responses`;
  const response = await axiosInstance.get(apiUrl);
  if (response){  
      data = (response.data && response.data.body) ? response.data.body : response.data
      return data
  }
  return data
}

export const getFocusGroupSessions = async (communityId: string) => {
  let data: FocusGroupSessionSchema[] = []
  let apiUrl = `/focus-group/${communityId}/sessions`;
  const response = await axiosInstance.get(apiUrl);
  if (response){
    data = (response.data && response.data.body) ? response.data.body : response.data
    return data
  }
  return data
}


export const fetchCommunitySelfServices = async (communityId: string) => {
  type SelfServiceDetailsMap = Record<string, SelfServiceDetailsSchema>
  let data: SelfServiceDetailsMap = {}
  // if (communityId){
    communityId = communityId || "default"
    let apiUrl = `/partner/${communityId}/self-service`;
    // const apiUrl = `/partner`;
    const response = await axiosInstance.get(apiUrl);
    if (response){
      data = (response.data && response.data.body) ? response.data.body : response.data
      return data
    }
  // }
  return data
}


export const addSelfServiceRegistration = async (communityId: string, selfServiceRegistration: RegisteredPartner) => {
  let data: RegisteredPartner
  communityId = communityId || "default"
  if (selfServiceRegistration && selfServiceRegistration.email && communityId){
    // communityId = "default"
    let apiUrl = `/partner/${communityId}`;
    // const apiUrl = `/partner`;
    const response = await axiosInstance.post(apiUrl, selfServiceRegistration);
    if (response){
      data = (response.data && response.data.body) ? response.data.body : response.data
      return data
    }
  }
  return null
}

export const getSelfServiceRegistrations = async (communityId: string, eventCode: string) => {
  let data: RegisteredPartner[] = []
  // let apiUrl = `/partner/${communityId}/events/${eventId}/registrations`;
  // let apiUrl = `/partner/search?event_code=${eventCode}`;
  communityId = communityId || "default"
  if (communityId && eventCode){
    const apiUrl = `/partner/${communityId}/search/${eventCode}`;
    // const apiUrl = `/partner/search/${eventCode}`;
    const response = await axiosInstance.get(apiUrl);
  if (response){
      data = (response.data && response.data.body) ? response.data.body : response.data
      return data
    }
  }
  return []
}

export const submitUATResponse = async (email_address: string, uatResponse: UATResponse) => {
  let apiUrl = `/uat/${email_address}`;
  try {
    let response = await axiosInstance.post(apiUrl, uatResponse);
    if (response) {
      return response.data;
    }
  } catch (err) {
    console.log(err);
    return null;
  }
};


export const addPaymentSetupIntent = async (uid: string) => {
  if (uid){
      let fetchSetupIntentApiUrl = `/community/setupintent/${uid}`
      try {
          let response = await axiosInstance.post(fetchSetupIntentApiUrl)
          if (response && response.data){
              console.log("create success");
              let data = (response.data && response.data.body) ? response.data.body : response.data
              return data
          }
      } catch (err) {
          console.log(err);
      }
  }
  return null
}

export const deletePaymentMethod = async (paymentMethodId: string) => {
  if (paymentMethodId){
      let fetchPaymentMethodApiUrl = `/community/paymethods/${paymentMethodId}`
      try {
          var accessToken: any = localStorage.getItem('accessToken')
          axiosInstance.defaults.headers.common['accessToken'] = accessToken
          
          var refreshToken: any = localStorage.getItem('refreshToken')
          axiosInstance.defaults.headers.common['refreshToken'] = refreshToken

          let response = await axiosInstance.delete(fetchPaymentMethodApiUrl)
          if (response) {
              console.log("create success");
              return response.data
          }
      } catch (err) {
          console.log(err);
          return null
      }
  }
}

export {
  startNewAuth, getAuthUser, verifyUserOTP, signOut, updateUserData, 
  createNewCommunity, fetchCommunity, getCommunityAllInventory,
  getAllKitchens, getKitchenID, addKitchen, deleteKitchen, updateKitchen, updateKitchenOptions,
  addCommunityMember, fetchMemberByID,updateCommunityMembership,addCommunityMembers,
  getMemberships, getMembershipRequests, 
  fetchMemberCredentials, addMembershipCredentials, getMemberReviews,
  getMembershipPlans,addCommunityPlans, getMembershipPlan,
  getCommunityRequirements, addMembershipRequirement, deleteMembershipRequirement,
  getPreferences, createPreference, updatePreferences,
  createBooking, fetchCommunityBookings, fetchBookingByID, updateBookingData,
  generateAIResponse, fetchPaymentMethods, 
  sendMessageToUser, getUserMessages, getAllMessages
};
